import React, { useState, useCallback, useMemo } from "react";
import {
    Drawer,
    List,
    ListItem,
    ListItemText,
    Box,
    ListItemButton,
    Button,
    Typography,
    Avatar,
    Collapse,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LogoutIcon from "@mui/icons-material/Logout";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useLocation } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import { getAccessToken } from '../services/authService';
import { tokens } from '../theme/tokens';

// ---------- COLORS (flat white sidebar + navy accents) ----------
const COLORS = {
    navBg: "#ffffff",
    border: tokens.color.border,
    shadow: "0 1px 3px rgba(15,23,42,0.08)",
    headerBg: tokens.color.brandDark,    // navy strip matching the top app bar
    brandText: tokens.color.brandDark,   // navy header text
    brandBg: tokens.color.brand,         // navy avatar
    navText: tokens.color.navText,       // default item text (navy-ish)
    navActiveText: tokens.color.brand,   // navy active text
    navActiveBg: tokens.color.navActiveBg,
    navHoverBg: tokens.color.navHoverBg,
    iconCircleBg: tokens.color.surfaceAlt,
    logoutBtnBg: tokens.color.brand,
    logoutBtnColor: "#fff",
    logoutBtnHover: tokens.color.brandDark,
    rightRadius: 0,
};
// ---------- END COLORS ----------

export interface MenuItem {
    text: string;
    icon: React.ElementType;
    path?: string;
    children?: MenuItem[];
    color?: string;
    isNew?: boolean;
}

// Small "NEW" pill shown next to recently-added menu entries.
const NewBadge: React.FC = () => (
    <Box
        component="span"
        sx={{
            ml: 0.75,
            px: 0.6,
            py: 0.05,
            fontSize: 8.5,
            fontWeight: 800,
            letterSpacing: 0.5,
            lineHeight: 1.6,
            borderRadius: 1,
            color: "#0f2e26",
            background: "linear-gradient(90deg,#38f9d7,#43e97b)",
            boxShadow: "0 1px 4px rgba(56,249,215,0.5)",
            textTransform: "uppercase",
            flexShrink: 0,
        }}
    >
        New
    </Box>
);

/** True if the item or (for parents) any descendant is flagged new. */
const hasNew = (item: MenuItem): boolean =>
    !!item.isNew || (item.children?.some((c) => hasNew(c)) ?? false);

export interface RichNavDrawerProps {
    menuItems: MenuItem[];
    onLogout: () => void;
    drawerWidth?: number;
    /** Mobile only: controls the temporary drawer's open state. */
    mobileOpen?: boolean;
    /** Mobile only: called to close the temporary drawer (backdrop/nav). */
    onMobileClose?: () => void;
}

const CircularIcon: React.FC<{
    icon: React.ElementType;
    selected?: boolean;
    size?: number;
    color?: string;
}> = ({
          icon,
          selected = false,
          size = 32,
          color = "#1ab98a"
      }) => (
    <Box
        sx={{
            width: size,
            height: size,
            borderRadius: "50%",
            bgcolor: COLORS.iconCircleBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 1,
            flexShrink: 0,
            border: `1.5px solid ${selected ? color : `${color}33`}`,
        }}
    >
        {React.createElement(icon, {
            style: {
                color: color,
                fontSize: size === 32 ? 18 : 16,
                opacity: selected ? 1 : 0.85,
            },
        })}
    </Box>
);

const RichNavDrawer: React.FC<RichNavDrawerProps> = ({
                                                         menuItems,
                                                         onLogout,
                                                         drawerWidth = 200,
                                                         mobileOpen = false,
                                                         onMobileClose,
                                                     }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

    const handleToggle = useCallback((text: string) => {
        setOpenMenus((prev) => ({ ...prev, [text]: !prev[text] }));
    }, []);

    const handleMenuItemClick = (item: any) => {
        if (item.isExternal && item.path === "/queues") {
            const token = getAccessToken();
            if (!token) {
                console.error("No token found. Please log in again.");
                return;
            }
            const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
            const url = `${baseUrl}/api/admin/queues?token=${token}`;

            window.open(url, '_blank');
        } else {
            // Standard internal navigation
            navigate(item.path);
        }
        // On mobile the drawer is a temporary overlay — close it after a choice.
        onMobileClose?.();
    };

    const renderMenuItem = useCallback(
        (item: MenuItem, nested = false) => {
            const isSelected = !!item.path && location.pathname.startsWith(item.path);

            if (item.children && item.children.length > 0) {
                const childSelected = item.children.some(
                    (c) => c.path && location.pathname.startsWith(c.path)
                );
                const isMenuOpen = !!openMenus[item.text];

                return (
                    <React.Fragment key={item.text}>
                        <ListItemButton
                            onClick={() => handleToggle(item.text)}
                            selected={childSelected}
                            aria-expanded={isMenuOpen}
                            aria-controls={`submenu-${item.text}`}
                            sx={{
                                mx: 1,
                                my: 0.2,
                                borderRadius: 2,
                                minHeight: 40,
                                px: 1.5,
                                background: childSelected ? COLORS.navActiveBg : "transparent",
                                color: childSelected ? COLORS.navActiveText : COLORS.navText,
                                fontWeight: childSelected ? 700 : 500,
                                "&:hover": { background: COLORS.navHoverBg },
                                "&.Mui-selected, &.Mui-selected:hover": { background: COLORS.navActiveBg },
                            }}
                        >
                            <CircularIcon
                                icon={item.icon}
                                selected={isSelected}
                                size={28}
                                color={item.color}
                            />
                            <ListItemText
                                primary={
                                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                                        {item.text}
                                        {hasNew(item) && <NewBadge />}
                                    </Box>
                                }
                                primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                            />
                            {isMenuOpen ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>

                        <Collapse
                            in={isMenuOpen}
                            timeout="auto"
                            unmountOnExit
                            sx={{ ml: 4 }}
                            id={`submenu-${item.text}`}
                        >
                            <List disablePadding>
                                {item.children.map((child) => renderMenuItem(child, true))}
                            </List>
                        </Collapse>
                    </React.Fragment>
                );
            }

            if (!item.path) return null; // safety check

            return (
                <ListItem key={item.text} disablePadding>
                    <ListItemButton
                        onClick={() => handleMenuItemClick(item)}
                        selected={isSelected}
                        sx={{
                            mx: 1,
                            my: 0.2,
                            borderRadius: 2,
                            minHeight: nested ? 34 : 38,
                            px: nested ? 2 : 1.5,
                            background: isSelected ? COLORS.navActiveBg : "transparent",
                            color: isSelected ? COLORS.navActiveText : COLORS.navText,
                            fontWeight: isSelected ? 700 : 500,
                            "&:hover": { background: COLORS.navHoverBg },
                            "&.Mui-selected, &.Mui-selected:hover": { background: COLORS.navActiveBg },
                        }}
                    >
                        <CircularIcon
                            icon={item.icon}
                            selected={isSelected}
                            size={28}
                            color={item.color}
                        />
                        <ListItemText
                            primary={
                                <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                                    {item.text}
                                    {item.isNew && <NewBadge />}
                                </Box>
                            }
                            primaryTypographyProps={{ fontSize: 12 }}
                        />
                    </ListItemButton>
                </ListItem>
            );
        },
        [location.pathname, openMenus, handleToggle, handleMenuItemClick]
    );

    const renderedMenu = useMemo(
        () => menuItems.map((item) => renderMenuItem(item)),
        [menuItems, renderMenuItem]
    );

    return (
        <Drawer
            variant={isMobile ? "temporary" : "permanent"}
            open={isMobile ? mobileOpen : undefined}
            onClose={onMobileClose}
            ModalProps={{ keepMounted: true }}
            sx={{
                // Reserve layout width only for the permanent (desktop) drawer;
                // the temporary one overlays content and reserves nothing.
                width: { xs: 0, md: drawerWidth },
                flexShrink: 0,
                zIndex: 1400,
                [`& .MuiDrawer-paper`]: {
                    width: drawerWidth,
                    boxSizing: "border-box",
                    background: COLORS.navBg,        // flat white — no gradient/glass
                    boxShadow: COLORS.shadow,
                    borderRight: `1px solid ${COLORS.border}`,
                    color: COLORS.navText,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    zIndex: 1400,
                },
            }}
        >
            {/* Navy header strip — same height & colour as the top app bar so the
                sidebar continues the bar instead of overlapping it with white. */}
            <Box
                sx={{
                    height: tokens.layout.appBarHeight,
                    flexShrink: 0,
                    bgcolor: COLORS.headerBg,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                }}
            >
                <Avatar sx={{ bgcolor: "rgba(255,255,255,0.16)", width: 32, height: 32 }}>
                    <DirectionsCarFilledIcon sx={{ fontSize: 18, color: "#fff" }} />
                </Avatar>
                <Typography
                    variant="subtitle1"
                    fontWeight={800}
                    sx={{ fontSize: 14, letterSpacing: 0.5, color: "#fff" }}
                >
                    SwiftaGo Admin
                </Typography>
            </Box>

            {/* Scrollable menu area — flexes to fill remaining height */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    "&::-webkit-scrollbar": { width: 6 },
                    "&::-webkit-scrollbar-thumb": {
                        background: "rgba(15,23,42,0.18)",
                        borderRadius: 3,
                    },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                }}
            >
                <List dense>{renderedMenu}</List>
            </Box>

            {/* Pinned logout (no longer absolute, so it never overlaps the menu) */}
            <Box
                sx={{
                    flexShrink: 0,
                    width: "100%",
                    textAlign: "center",
                    px: 1.5,
                    py: 2,
                    borderTop: `1px solid ${COLORS.border}`,
                }}
            >
                <Button
                    variant="contained"
                    startIcon={<LogoutIcon fontSize="small" />}
                    onClick={onLogout}
                    sx={{
                        bgcolor: COLORS.logoutBtnBg,
                        color: COLORS.logoutBtnColor,
                        fontWeight: 700,
                        fontSize: 12,
                        px: 2.5,
                        py: 0.5,
                        borderRadius: 2,
                        "&:hover": {
                            bgcolor: COLORS.logoutBtnHover,
                            color: "#fff",
                        },
                    }}
                >
                    Logout
                </Button>
            </Box>
        </Drawer>
    );
};

export default React.memo(RichNavDrawer);
